/**
 * Per-browser UI language selection.
 *
 * Shaped after services/activeFileStorage.js, with ONE deliberate divergence:
 * there is no Supabase / cross-device sync here, and that is not an oversight.
 * The active dataset is a piece of work state that should follow a user between
 * devices; the UI language is an ergonomic preference of the machine in front
 * of them. Someone presenting in German on a beamer while reading English on
 * their laptop must not have the two devices fight over one stored value.
 *
 * Nothing server-side consumes this value. API payloads stay language-neutral —
 * the backend returns data and stable identifiers, never translated display
 * text (see docs/i18n-migration.md, rule H2).
 */

import { SUPPORTED_LANGUAGES } from '../i18n/config';

const KEY = 'cintelion.uiLanguage';

/**
 * Returns the stored language, or null if none is stored or the stored value
 * is not one we support.
 *
 * The validation matters: a hand-edited localStorage entry, or a value left
 * behind by a future build that supported more languages, must not be able to
 * wedge i18next into a language we have no resources for. Callers treat null
 * as "fall back to DEFAULT_LANGUAGE".
 */
export const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(KEY);
    return SUPPORTED_LANGUAGES.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const setStoredLanguage = (language) => {
  try {
    if (SUPPORTED_LANGUAGES.includes(language)) {
      localStorage.setItem(KEY, language);
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    // localStorage unavailable (private mode quota etc.) — the switch still
    // applies to the current session via i18n.changeLanguage(), it just won't
    // survive a reload, which then falls back to DEFAULT_LANGUAGE.
  }
};
